import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, connect } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, injectIntl } from 'react-intl'
import * as auth from '../_redux/authRedux'
import * as authCrud from '../_redux/authCrud'

/*
 * Single Login v2 / Firebase login.
 *
 * Three entry paths, all ending in a quiz-app session:
 *
 *   1. email + password
 *        POST /api/auth/login_with_email_and_password
 *        (may return a business picker for multi-business accounts)
 *
 *   2. SSO popup
 *        The v2 auth app (usermodulev2frontend BusinessModal) posts
 *        JSON.stringify({ token, business_id, business_name }) to window.opener
 *        and closes itself.
 *
 *   3. Direct module click (no popup)
 *        Same BusinessModal, but when there is no window.opener it redirects the
 *        browser to `<redirecturl>?token=...&business_id=...`. We pick those up
 *        from the query string here.
 *
 * Paths 2 and 3 both hand over a module-auth token, exchanged via
 * /api/auth/verify_sso_token.
 *
 * Layout mirrors attendance-frontend's Login (same Metronic template): the
 * `login-form` class supplies the 450px max-width from login-1.scss.
 */

// Surface only the backend's safe, user-facing message; never raw axios text.
const friendlyError = (response) =>
	(response && response.message) ||
	'Something went wrong. Please contact support.'

// Full-page state for handoffs the user did not explicitly trigger (module
// click / popup return), where an idle-looking form would read as "broken".
function SigningIn({ message }) {
	return (
		<div className='login-form text-center'>
			<div className='spinner-border text-primary mb-5' role='status'>
				<span className='sr-only'>Loading...</span>
			</div>
			<h3 className='font-size-h4 font-weight-bold'>{message}</h3>
			<p className='text-muted font-weight-bold mt-2'>
				Please wait, this only takes a moment.
			</p>
		</div>
	)
}

function Login() {
	const dispatch = useDispatch()
	const location = useLocation()

	// '' | 'login' | 'business' | 'sso' | 'popup' | 'handoff'
	const [loading, setLoading] = useState('')
	const [error, setError] = useState('')

	// Multi-business handoff state.
	const [businessList, setBusinessList] = useState(null)
	const [ssoToken, setSsoToken] = useState(null)
	const [firebaseRefreshToken, setFirebaseRefreshToken] = useState(null)
	const [selected, setSelected] = useState(null)

	const loadToState = (authToken, refreshToken, user) => {
		dispatch(auth.actions.login(authToken, refreshToken, user))
	}

	const LoginSchema = Yup.object().shape({
		email: Yup.string()
			.email('Please enter a valid email')
			.required('Email is required'),
		password: Yup.string().required('Password is required'),
	})

	const formik = useFormik({
		initialValues: { email: '', password: '' },
		validationSchema: LoginSchema,
		onSubmit: (values) => login_with_email_and_password(values),
	})

	const getInputClasses = (field) => {
		if (formik.touched[field] && formik.errors[field]) return 'is-invalid'
		if (formik.touched[field] && !formik.errors[field]) return 'is-valid'
		return ''
	}

	const login_with_email_and_password = async (values) => {
		setError('')
		setLoading('login')
		let response = await authCrud.login_with_email_and_password({
			email: values.email,
			password: values.password,
		})
		response = response.data
		setLoading('')

		if (!response || !response.status) {
			setError(friendlyError(response))
			return
		}

		if (response.requires_business_selection) {
			setBusinessList(response.business_list || [])
			setSsoToken(response.sso_token)
			setFirebaseRefreshToken(response.firebase_refresh_token)
			return
		}

		loadToState(response.auth_token, response.refresh_token, response.user)
	}

	const businessSelected = async (business_id) => {
		setError('')
		setSelected(business_id)
		setLoading('business')
		let response = await authCrud.login_to_business_with_token({
			sso_token: ssoToken,
			firebase_refresh_token: firebaseRefreshToken,
			business_id,
		})
		response = response.data
		setLoading('')
		setSelected(null)

		if (!response || !response.status) {
			setError(friendlyError(response))
			return
		}

		loadToState(response.auth_token, response.refresh_token, response.user)
	}

	// Shared tail for every SSO handoff: unwrap, surface errors, load session.
	const completeHandoff = useCallback(async (request, mode) => {
		setError('')
		setLoading(mode)
		let response = await request
		response = response.data
		setLoading('')

		if (!response || !response.status) {
			setError(friendlyError(response))
			return
		}

		loadToState(response.auth_token, response.refresh_token, response.user)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Exchange a v2 module-auth token (popup postMessage or redirect handoff).
	const exchangeSsoToken = useCallback(
		(token, business_id, mode) =>
			completeHandoff(authCrud.verify_sso_token({ token, business_id }), mode),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	// Exchange app keys. This is what the module-click flow sends: clicking a
	// module in the SingleLogin user module opens this app in a new tab and
	// postMessages { publicKey, privateKey } - there is no token on that path.
	const exchangeAppKeys = useCallback(
		(public_key, private_key, mode) =>
			completeHandoff(
				authCrud.login_using_keys({ public_key, private_key }),
				mode,
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	/* ------------------------------------------------------------------ *
	 * Path 3: direct module click - token arrives in the query string.
	 * ------------------------------------------------------------------ */
	useEffect(() => {
		const params = new URLSearchParams(location.search)
		const token = params.get('token')
		if (!token) return

		const business_id = params.get('business_id')

		// Strip the token from the address bar before the exchange so it is not
		// left in history, bookmarks, or a copied URL.
		window.history.replaceState({}, document.title, window.location.pathname)

		exchangeSsoToken(token, business_id, 'handoff')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/* ------------------------------------------------------------------ *
	 * Path 2: SSO popup postMessage.
	 * ------------------------------------------------------------------ */
	useEffect(() => {
		const trustedOrigins = (process.env.REACT_APP_SSO_TRUSTED_ORIGINS || '')
			.split(',')
			.map((value) => value.trim())
			.filter(Boolean)

		try {
			trustedOrigins.push(new URL(process.env.REACT_APP_USER_MODULE_URL).origin)
		} catch (e) {
			// REACT_APP_USER_MODULE_URL is not a full URL; rely on the explicit list.
		}

		const handler = (event) => {
			// Only messages from the auth app may mint a session - an unchecked
			// listener would let any page log a user in.
			if (!trustedOrigins.includes(event.origin)) return

			let data = event.data || {}
			// BusinessModal posts a JSON string, not an object.
			if (typeof data === 'string') {
				try {
					data = JSON.parse(data)
				} catch (e) {
					return
				}
			}

			// Two handoff shapes, depending on how the user got here:
			//   - module click  -> { type:'SSO_LOGIN', publicKey, privateKey }
			//     (usermodulev2frontend handleSSOLogin, sent ~1.5s after the tab
			//      opens, which is why the login form flashes first)
			//   - business pick -> { token, business_id }  (BusinessModal)
			const publicKey = data.publicKey || data.public_key
			const privateKey = data.privateKey || data.private_key

			if (publicKey && privateKey) {
				exchangeAppKeys(publicKey, privateKey, 'handoff')
			} else if (data.token) {
				exchangeSsoToken(data.token, data.business_id, 'handoff')
			}
		}

		window.addEventListener('message', handler, false)
		return () => window.removeEventListener('message', handler, false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const openSsoPopup = () => {
		setError('')
		setLoading('popup')

		const popup = window.open(
			process.env.REACT_APP_USER_MODULE_URL,
			'SingleLoginPopup',
			'width=500,height=600',
		)

		if (!popup) {
			setLoading('')
			setError(
				'Could not open the SingleLogin window. Please allow pop-ups for this site and try again.',
			)
			return
		}

		// Clear the spinner if the user closes the popup without finishing -
		// otherwise the button would spin forever.
		const timer = setInterval(() => {
			if (popup.closed) {
				clearInterval(timer)
				// Leave a handoff in progress alone; only cancel the waiting state.
				setLoading((current) => (current === 'popup' ? '' : current))
			}
		}, 500)
	}

	/* --------------------------- handoff in flight --------------------------- */
	if (loading === 'handoff') {
		return <SigningIn message='Signing you in with SingleLogin…' />
	}

	/* ------------------------------ business picker -------------------------- */
	if (businessList) {
		return (
			<div className='login-form login-signin'>
				<div className='text-center mb-10 mb-lg-20'>
					<h3 className='font-size-h1'>Select a Business</h3>
					<p className='text-muted font-weight-bold'>
						This account belongs to more than one business
					</p>
				</div>

				{error && (
					<div className='mb-10 alert alert-custom alert-light-danger alert-dismissible'>
						<div className='alert-text font-weight-bold'>{error}</div>
					</div>
				)}

				{businessList.length === 0 ? (
					<div className='text-center text-muted font-weight-bold'>
						No business is linked to this account. Please contact the
						administrator.
					</div>
				) : (
					businessList.map((business) => (
						<div
							key={business.id}
							onClick={() =>
								loading !== 'business' && businessSelected(business.id)
							}
							className='d-flex align-items-center mb-3 bg-primary-o-20 bg-hover-state-primary rounded-sm p-3 text-dark text-hover-light cursor-pointer'
							style={{
								cursor: loading === 'business' ? 'not-allowed' : 'pointer',
								opacity:
									loading === 'business' && selected !== business.id ? 0.5 : 1,
							}}
						>
							<div className='symbol symbol-40 symbol-light-primary mr-5'>
								<span className='symbol-label btn btn-icon btn-light'>
									{selected === business.id && loading === 'business' ? (
										<span className='spinner-border spinner-border-sm align-middle'></span>
									) : (
										<i className='flaticon2-website'></i>
									)}
								</span>
							</div>
							<div className='d-flex flex-column font-weight-bold'>
								<span className='font-size-lg text-capitalize'>
									{business.name}
								</span>
							</div>
						</div>
					))
				)}

				<div className='form-group d-flex justify-content-center mt-10'>
					<button
						type='button'
						className='btn btn-light-primary font-weight-bold px-9 py-4'
						disabled={loading === 'business'}
						onClick={() => {
							setBusinessList(null)
							setSsoToken(null)
							setFirebaseRefreshToken(null)
							setError('')
						}}
					>
						Back
					</button>
				</div>
			</div>
		)
	}

	/* --------------------------------- login --------------------------------- */
	const busy = loading !== ''

	return (
		<div className='login-form login-signin' id='kt_login_signin_form'>
			<div className='text-center mb-10 mb-lg-20'>
				<h3 className='font-size-h1'>Sign In To QuizApp</h3>
				<p className='text-muted font-weight-bold'>Enter your login details</p>
			</div>

			<div className='text-center mb-5'>
				<button
					type='button'
					id='kt_login_signin_submit_sso'
					className='btn btn-primary btn-block'
					onClick={openSsoPopup}
					disabled={busy}
				>
					{loading === 'popup' ? (
						<>
							<span className='spinner-border spinner-border-sm align-middle mr-3'></span>
							Waiting for SingleLogin…
						</>
					) : (
						'Sign in with SingleLogin.io'
					)}
				</button>
			</div>

			<form
				onSubmit={formik.handleSubmit}
				className='form fv-plugins-bootstrap fv-plugins-framework'
			>
				{error && (
					<div className='mb-10 alert alert-custom alert-light-danger alert-dismissible'>
						<div className='alert-text font-weight-bold'>{error}</div>
					</div>
				)}

				<div className='form-group fv-plugins-icon-container'>
					<input
						placeholder='Email'
						type='email'
						autoComplete='username'
						disabled={busy}
						className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
							'email',
						)}`}
						name='email'
						{...formik.getFieldProps('email')}
					/>
					{formik.touched.email && formik.errors.email && (
						<div className='fv-plugins-message-container'>
							<div className='fv-help-block'>{formik.errors.email}</div>
						</div>
					)}
				</div>

				<div className='form-group fv-plugins-icon-container'>
					<input
						placeholder='Password'
						type='password'
						autoComplete='current-password'
						disabled={busy}
						className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
							'password',
						)}`}
						name='password'
						{...formik.getFieldProps('password')}
					/>
					{formik.touched.password && formik.errors.password && (
						<div className='fv-plugins-message-container'>
							<div className='fv-help-block'>{formik.errors.password}</div>
						</div>
					)}
				</div>

				<div className='form-group d-flex flex-wrap justify-content-between align-items-center'>
					<Link
						to='/auth/forgot-password'
						className='text-dark-50 text-hover-primary my-3 mr-2'
						id='kt_login_forgot'
					>
						<FormattedMessage
							id='AUTH.GENERAL.FORGOT_BUTTON'
							defaultMessage='Forgot Password'
						/>
					</Link>

					<button
						id='kt_login_signin_submit'
						type='submit'
						disabled={busy}
						className='btn btn-primary font-weight-bold px-9 py-4 my-3'
					>
						<span>{loading === 'login' ? 'Signing in…' : 'Sign In'}</span>
						{loading === 'login' && (
							<span className='ml-3 spinner-border spinner-border-sm align-middle'></span>
						)}
					</button>
				</div>
			</form>
		</div>
	)
}

export default injectIntl(connect(null, auth.actions)(Login))
